import React from "react";

import { SimpleModalProps } from "@/components_v2/Modal/types";
import { RowSelectionProps } from "@/components_v2/Tables/types";
import { ColumnType } from "antd/es/table/interface";
import { BaseErrorProps } from "@/components_v2/FormFields/types";
import { FormFieldValue, FormFieldWithName } from "@/domains/datablocks/types";
import { FileProgressStatus } from "@/features/files/list/components/CreateDataFileModal";
import { UploadFile } from "antd/es/upload/interface";
import { EvidenceColumnKey } from "@/domains/evidence/types";
import {
  DataBlockListResponse,
  DataBlockRequest,
  DataBlockResponse,
  DataTemplateResponse,
  ValidationRuleResponse,
} from "@/redux/datablocks/types";
import { BaseFilters, BasePaginationQueryParams } from "@/redux/types";
import { FullUserResponse } from "@/redux/users/types";

import { DataBlockRequestEditModalsState } from "@/features/evidence-requests/list/types";

export interface DataBlocksFiltersState extends BaseFilters {
  ids: number[];
  dataTemplates: number[];
  sharingRights: string[];
  contributors: number[];
  languages: number[];
  search?: string;
  user?: number;
  organizations?: number[];
  excludeOrganizations?: number[];
  prioritizeQuestion?: number;
  editable: true;
}

export interface DataBlockReference {
  label: string;
  id?: number;
  surveyId?: number;
  discussionId?: number;
  uuid?: string;
}

export interface DataBlockReferences {
  surveys: DataBlockReference[];
  surveyViews: DataBlockReference[];
  discussions: DataBlockReference[];
  comments: DataBlockReference[];
  chapters: DataBlockReference[];
}

export type DataBlocksQueryParams = BasePaginationQueryParams & {
  isExample?: boolean;
  ids?: number | number[] | null;
  dataTemplates?: number | number[] | null;
  sharingRights?: string | string[] | null;
  contributors?: number | number[] | null;
  languages?: number | number[] | null;
  search?: string;
  user?: number;
  organizations?: number | number[] | null;
  excludeOrganizations?: number | number[] | null;
  prioritizeQuestion?: number;
  editable?: boolean;
  excludeCreators?: number | number[] | null;
};

export type FilterKey =
  | "type"
  | "sharingRights"
  | "contributors"
  | "organizations"
  | "languages"
  | "search";

export type Tabs = {
  key: string;
  title: string;
  filters: FilterKey[];
  columns: EvidenceColumnKey[];
  defaultFilters: Record<string, any>;
};

export interface DataBlockTypeStepProps extends BaseErrorProps {
  templates: DataTemplateResponse[];
  activeTemplateId?: number;
  handleTemplateChange: (value: number) => void;
}

export interface DataBlockDetailsStepProps {
  dataTemplate: DataTemplateResponse;
  excludeFields?: (string | number)[];
}

export interface DataBlockDefaultValues {
  dataTemplate?: number;
  request?: DataBlockRequest;
}

export interface CreateDataBlockProps extends SimpleModalProps {
  onSubmit?: (data: DataBlockResponse) => void;
  defaultValues?: DataBlockDefaultValues;
  filters?: Partial<DataBlocksFiltersState>;
}

export type CreateDataBlockRequest = {
  dataTemplate: number;
  fieldValues: FormFieldValue[];
};

export type DataBlockFormFields = CreateDataBlockRequest & {
  organization: number;
  sharingRights: string;
  request?: number;
};

export interface CreateDataBlockModalFooterProps {
  currentStep: number;
  isSubmitting: boolean;
  isNextEnabled: boolean;
  handleBack: () => void;
  handleNext: (value?: boolean) => void;
}

export interface DataBlockActionButtonsProps {
  dataBlock: DataBlockResponse;
  dataTemplate: DataTemplateResponse;
  setOpenDeleteModal: (value: boolean) => void;
  setEditRequestModal: (value: DataBlockRequestEditModalsState) => void;
}

export interface DataBlockSidebarProps {
  dataBlock: DataBlockResponse;
  dataTemplate: DataTemplateResponse;
  showCtas?: boolean;
}

export interface DataBlockContentProps {
  dataBlock: DataBlockResponse | DataBlockListResponse;
  dataTemplate: DataTemplateResponse;
}

export interface DataBlockDetailsProps {
  dataBlock: DataBlockResponse | DataBlockListResponse;
  dataTemplate: DataTemplateResponse;
}

export interface AddContributorModalProps extends SimpleModalProps {
  dataBlock: DataBlockResponse;
  currentContributorIds: number[];
}

export interface AddContributorsFormValues {
  contributors: number[];
}

export interface DataBlockReferenceType {
  title: string;
  icon: React.FunctionComponent;
  getLink: (value: DataBlockReference) => string;
}

export type DataBlockReferencesTypes = {
  surveys: DataBlockReferenceType;
  surveyViews: DataBlockReferenceType;
  discussions: DataBlockReferenceType;
  comments: DataBlockReferenceType;
  chapters: DataBlockReferenceType;
};

export interface ValidationRuleForSharingRights
  extends Omit<ValidationRuleResponse, "id" | "ruleFields"> {
  id?: number;
  ruleFields?: number[];
}

export interface FormFieldsWithSharingRights
  extends Omit<FormFieldWithName, "id" | "rules"> {
  id: string | number;
  rules?: ValidationRuleForSharingRights[];
}

export interface DataBlockReferencesProps {
  dataBlock: DataBlockResponse;
}

export interface DataBlockRequestsTableProps<T> extends RowSelectionProps {
  columnNames: string[];
  defaultFilters?: Partial<DataBlocksFiltersState>;
  filterNames: string[];
  additionalColumns?: Record<string, ColumnType<T>>;
  className?: string;
}

export type DataBlockPermissionsOptions = {
  dataBlock?: DataBlockResponse;
  dataTemplate?: DataTemplateResponse;
  currentUser?: FullUserResponse | null;
};

export enum ViewTypes {
  Tabs = "TABS",
  RequestsForm = "REQUESTS_FORM",
  EditRequestForm = "EDIT_REQUEST_FORM",
}

export enum activeTabs {
  dataBlocks = "dataBlocks",
  requests = "requests",
}

export interface SurveyDataBlockModalProps extends SimpleModalProps {
  selectedBlocks: DataBlockResponse[];
  setSelectedBlocks: (value: DataBlockResponse[]) => void;
  filters: Partial<DataBlocksFiltersState>;
  initialData: Record<string, any>;
}

export interface DataBlocksTabProps {
  selectedBlocks: DataBlockResponse[];
  setSelectedBlocks: (value: DataBlockResponse[]) => void;
  currentQuestion?: number;
  filters: Partial<DataBlocksFiltersState>;
  onEditRequestClick: (value: number) => void;
  onCreateDataBlockClick: () => void;
}

export type FileProgressStatusType =
  (typeof FileProgressStatus)[keyof typeof FileProgressStatus];

export interface SelectedFile {
  name: string;
  status: FileProgressStatusType;
  file: UploadFile;
  error?: string;
}
